import { t } from '../../../../i18n';
import { WorkbenchAlert } from '../types';

/**
 * 告警卡展示文案的本地拼装。
 *
 * 上游只下发 `alertTypeCode`（= basic 的 `subType`）+ `params` 结构化数值，
 * 成品文案由前端按 `workbench.alertTypes.<code>.*` 渲染三语。
 *
 * `riskTag` 与 `recommendedAction` 后端恒为空（plan W12：上游无数据源），
 * 但它们是**告警类型的固有属性**而非逐条变化的数据，故按 code 出静态文案是合理的 ——
 * 真正逐条变化的量级（件数 / 比率 / 涉及对象数）走 `params` 插值。
 *
 * todo hetianyang 当前文案是**临时占位**，等产品给定稿后替换 `workbench.alertTypes.*`。
 *   `triggerReason`（诊断依据）无法本地编造，仍为空 —— PRD §4.1(3) 要求 Agent 必须输出，
 *   这项只能等上游补（plan W12）
 */

/**
 * 后端 params 里的比率是 0~1 小数，展示要百分数。
 *
 * 保留两位小数：退件率这类低比率取整后会被抹成「0%」，与卡片「退件率偏高」的结论直接打架。
 */
const toPercent = (raw?: string): string | null => {
    if (!raw) return null;
    const value = Number(raw);
    if (!Number.isFinite(value)) return null;
    return (value * 100).toFixed(2);
};

const toInt = (raw?: string): number | null => {
    if (!raw) return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
};

/**
 * 派生插值参数。
 *
 * i18n 模板不能做算术，故百分比换算在此算好再插值。
 */
const buildInterpolation = (params: Record<string, string>): Record<string, string | number> => {
    return {
        ...params,
        ratePercent: toPercent(params.rate) ?? '—',
        baselinePercent: toPercent(params.baselineRate) ?? '—',
        returnRatePercent: toPercent(params.returnRate) ?? '—',
        dspName: params.dspName ?? '—',
        totalCount: params.totalCount ?? '—',
        abnormalCount: params.abnormalCount ?? '—',
    };
};

/**
 * 取某一段文案。
 *
 * 未识别的 `alertTypeCode` 回落到后端的兜底文本字段 —— 新告警类型会先于前端发版出现，
 * 此时显示裸 code 对仓管毫无意义。当前后端兜底字段恒为空，故最终回落到空串（不渲染该段）。
 */
/** 拼文案只用得到这两个字段；hub 日报的风险项是更窄的结构，不必凑成完整 WorkbenchAlert */
type AlertTextSource = Pick<WorkbenchAlert, 'alertTypeCode' | 'params'>;

const segment = (
    alert: AlertTextSource,
    key: 'label' | 'value' | 'headline' | 'riskTag' | 'recommendedAction' | 'handleAction',
    fallback?: string | null
): string => {
    if (!alert.alertTypeCode) {
        return fallback ?? '';
    }
    const composed = String(
        t(`workbench.alertTypes.${alert.alertTypeCode}.${key}`, {
            ...buildInterpolation(alert.params ?? {}),
            defaultValue: '',
        })
    );
    return composed || fallback || '';
};

/** 指标名，如「领取率」「丢件异常」 */
export const composeAlertLabel = (
    alert: AlertTextSource & Pick<WorkbenchAlert, 'metricLabel'>
): string => segment(alert, 'label', alert.metricLabel);

/** 指标展示值，如「62%」「14 件」 */
export const composeAlertValue = (alert: WorkbenchAlert): string =>
    segment(alert, 'value', alert.metricValueText);

/**
 * 一句话结论，如「TOP 3 路线异常」。
 *
 * `abnormalCount` 用卡上实际列出的明细条数覆盖上游 params：上游按 top-N 截断后才下发，
 * 其 params 是全量口径，直接用会出现「5 DSP」却只列了 3 行。无明细时不覆盖 —— 出「0」更糟。
 */
export const composeAlertHeadline = (alert: WorkbenchAlert): string =>
    segment(
        alert.entities.length > 0
            ? { ...alert, params: { ...alert.params, abnormalCount: String(alert.entities.length) } }
            : alert,
        'headline',
        alert.headline
    );

/**
 * 结论行后缀「涉及 5 名司机」。
 *
 * 只要卡上出现司机就出这段，不限告警类型、不限司机挂在哪一层：投诉卡的司机是顶层明细，
 * 丢件 / 退件率 / 断更卡的司机在 DSP 行的下钻里。按司机 id 去重 —— 上游不下发汇总值，
 * 跨 DSP 重复的司机只算一次。
 *
 * `entityType` 大小写两套口径：顶层是上游原样透传，子明细经后端 `DIMENSION_TO_ENTITY_TYPE`
 * 归一为大写。故比较前先归一，否则上游改口径时会静默数成 0、整段不出。
 */
export const composeAlertDriverScope = (alert: WorkbenchAlert): string => {
    const driverIds = new Set(
        alert.entities
            .flatMap((entity) => [entity, ...(entity.subEntities ?? [])])
            .filter((node) => node.entityType?.toUpperCase() === 'DRIVER')
            .map((node) => node.entityId)
    );
    return driverIds.size === 0
        ? ''
        : String(t('workbench.alertText.driverScope', { drivers: driverIds.size }));
};

/**
 * 处置按钮文案，如丢件异常的「已抽查线路」、断更异常的「已约谈 DSP」。
 *
 * 未给定文案的类型回落到通用「标记已处理」—— 处置动作名是告警类型的固有属性，
 * 但产品只给了部分类型的定稿说法，编不出来的不猜。
 */
export const composeAlertHandleAction = (alert: WorkbenchAlert): string =>
    segment(alert, 'handleAction') || String(t('workbench.markHandled'));

/** 风险标签，如「领取滞留」 */
export const composeAlertRiskTag = (
    alert: AlertTextSource & Pick<WorkbenchAlert, 'riskTag'>
): string => segment(alert, 'riskTag', alert.riskTag);

/**
 * 卡片配色档位。后端 `severity` 恒为 null（plan W12：上游无数据源），全部落到灰色兜底档，
 * 与设计稿的红 / 橙卡不符。档位与 riskTag 同理是告警类型的固有属性，故按 code 派生；
 * 上游哪天补了 severity 就以上游为准。未收录的类型仍走灰色档，不猜。
 */
const SEVERITY_BY_TYPE: Record<string, string> = {
    PICKUP_RATE: 'HIGH',
    DELIVERY_RATE: 'MEDIUM',
};

export const severityOf = (severity?: string | null, alertTypeCode?: string | null): string =>
    severity || (alertTypeCode && SEVERITY_BY_TYPE[alertTypeCode]) || '';

export const composeAlertSeverity = (alert: WorkbenchAlert): string =>
    severityOf(alert.severity, alert.alertTypeCode);

/** 建议动作 */
export const composeAlertRecommendedAction = (alert: WorkbenchAlert): string =>
    segment(alert, 'recommendedAction', alert.recommendedAction);

/**
 * 明细行的主值文案，如「6 件」。
 *
 * 单位是告警类型的固有属性（丢件数「件」、投诉数「起」），按 code 取；
 * 未配置单位的类型只出数字 —— 比率类告警的明细值口径未定，不猜单位。
 *
 * 上游是 `DECIMAL(_,4)`，`18.0000` 要去掉补零后才是仓管看的「18 件」。后端已在
 * `WorkbenchAlertConverter` 去零，这里再去一次是因为展示层不该依赖上游的小数位约定。
 */
export const composeEntityPrimary = (
    alert: WorkbenchAlert,
    primaryValue?: string | null
): string => {
    if (!primaryValue) {
        return '';
    }
    const numeric = Number(primaryValue);
    const text = Number.isFinite(numeric) ? String(numeric) : primaryValue;
    const unit = alert.alertTypeCode
        ? String(t(`workbench.alertTypes.${alert.alertTypeCode}.entityUnit`, { defaultValue: '' }))
        : '';
    return unit ? `${text} ${unit}` : text;
};

/**
 * 明细行的次要文案，如「7 天中 5 天」。
 *
 * 后端 `secondaryValue` 只给命中天数（数字），区间总天数由 `intervalStart`/`intervalEnd` 推算 ——
 * 上游区间不一定是 7 天（退件率比其他指标少一天），不能写死。
 */
export const composeEntitySecondary = (
    alert: WorkbenchAlert,
    secondaryValue?: string | null
): string => {
    const daysHit = toInt(secondaryValue ?? undefined);
    if (daysHit === null) {
        return secondaryValue ?? '';
    }
    const totalDays = intervalDays(alert.params?.intervalStart, alert.params?.intervalEnd);
    if (totalDays === null) {
        return String(t('workbench.alertText.daysHitOnly', { days: daysHit }));
    }
    return String(t('workbench.alertText.daysHitOfTotal', { days: daysHit, total: totalDays }));
};

/**
 * 明细行主值出比率（而非件数）的告警类型。
 *
 * 按 code 而非 `entityType` 判断：领取率卡的明细同为司机维度、上游也带 `rate`，
 * 但那张卡的主值仍是件数。收录方式同 [SEVERITY_BY_TYPE]。
 */
const RATE_AS_PRIMARY_TYPES = ['DELIVERY_RATE'];

export const entityPrimaryIsRate = (alert: WorkbenchAlert): boolean =>
    Boolean(alert.alertTypeCode && RATE_AS_PRIMARY_TYPES.includes(alert.alertTypeCode));

/** 明细行的比率文案，如「80%」。口径同卡片 `params.rate`，无值出「—」区分「没数据」与「渲染挂了」 */
export const formatEntityRate = (rate?: string | null): string => {
    const percent = toPercent(rate ?? undefined);
    return percent === null ? '—' : `${percent}%`;
};

/**
 * 停留时长文案：`1h20min`；超 24h 继续累加小时（`26h5min`），不进「天」——
 * 出「1d2h」仓管要多做一次心算。不足 1 分钟出 `<1min`，与「无数据」的 `—` 区分开。
 */
export const formatStayDuration = (stayMinutes?: number | null): string => {
    if (stayMinutes === null || stayMinutes === undefined || !Number.isFinite(stayMinutes)) {
        return '—';
    }
    if (stayMinutes < 1) {
        return '<1min';
    }
    const hours = Math.floor(stayMinutes / 60);
    const minutes = stayMinutes % 60;
    return hours > 0 ? `${hours}h${minutes}min` : `${minutes}min`;
};

/** 闭区间天数；解析失败返回 null，由调用方降级 */
const intervalDays = (start?: string, end?: string): number | null => {
    if (!start || !end) return null;
    const startMs = Date.parse(start);
    const endMs = Date.parse(end);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) {
        return null;
    }
    return Math.round((endMs - startMs) / 86400000) + 1;
};
